import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Home as HomeIcon,
  Create as CreateIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import UserAvatar from '../common/UserAvatar';

const drawerWidth = 280;

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  hideSidebar?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ 
  children, 
  title,
  showBackButton = false,
  onBack,
  hideSidebar = false
}) => {
  const { user, logout } = useAuth();
  const shouldShowSidebar = !hideSidebar && !!user;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const currentUsername = user?.username;

  const menuItems = [
    { icon: <HomeIcon />, label: 'Home', path: '/home' },
    { icon: <CreateIcon />, label: 'Create Opinion', path: '/create' },
    { icon: <PersonIcon />, label: 'My Opinions', path: '/my-opinions' },
    { icon: <PeopleIcon />, label: 'My Followers', path: user?._id ? `/profile/${user._id}/followers` : '/login' },
    { icon: <TrendingUpIcon />, label: 'Trending', path: '/trending' },
    { icon: <SettingsIcon />, label: 'Profile Settings', path: '/profile' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const sidebar = (
    <Box sx={{ 
      height: '100%', 
      background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Section */}
      <Box sx={{ p: 2.5, textAlign: 'center' }}>
        <Box
          component="img"
          src="/images/logo.png"
          alt="OpinLoop"
          onClick={() => navigate('/home')}
          sx={{
            height: { xs: 40, sm: 48 },
            width: 'auto',
            maxWidth: '100%',
            cursor: 'pointer',
            objectFit: 'contain',
            borderRadius: 2,
            mb: 1,
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', mt: 0.5 }}>
          Share Your Perspective
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

      {/* User Profile Section */}
      {user && (
        <>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <UserAvatar
                profileIcon={user.profilePicture}
                username={currentUsername}
                size={44}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  mr: 1.5
                }}
              />
              <Box>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500, fontSize: '0.9375rem' }}>
                  {currentUsername || 'User'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8125rem' }}>
                  Active Member
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
        </>
      )}

      {/* Navigation Menu */}
      <List sx={{ px: 1.5, py: 1, flex: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path.includes('/followers') && location.pathname.includes('/followers'));
          
          return (
            <Box
              key={item.label}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.25,
                mb: 0.75,
                borderRadius: 1.5,
                cursor: 'pointer',
                color: isActive ? 'white' : 'rgba(255,255,255,0.85)',
                background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                '&:hover': {
                  background: 'rgba(255,255,255,0.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                {item.icon}
              </Box>
              <Typography variant="body1" sx={{ fontWeight: isActive ? 500 : 400, fontSize: '0.9375rem' }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </List>

      {/* Logout Button */}
      {user && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: 2 }} />
          <Box sx={{ px: 1.5, pb: 2, pt: 1 }}>
            <Box
              onClick={async () => {
                await logout();
                navigate('/');
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.25,
                borderRadius: 1.5,
                cursor: 'pointer',
                color: 'rgba(255,255,255,0.9)',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  borderColor: 'rgba(255,255,255,0.3)',
                  transform: 'translateX(4px)'
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                <LogoutIcon />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9375rem' }}>
                Logout
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Fixed Sidebar - Desktop */}
      {shouldShowSidebar && (
        <Box
          component="nav"
          sx={{
            width: { md: drawerWidth },
            flexShrink: { md: 0 },
            display: { xs: 'none', md: 'block' }
          }}
        >
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                borderRight: 'none',
                background: 'transparent'
              },
            }}
            open
          >
            {sidebar}
          </Drawer>
        </Box>
      )}

      {/* Mobile Drawer */}
      {shouldShowSidebar && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {sidebar}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: shouldShowSidebar ? `calc(100% - ${drawerWidth}px)` : '100%' },
          background: 'linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)'
        }}
      >
        {/* Mobile App Bar */}
        {(isMobile || showBackButton) && (
          <AppBar
            position="fixed"
            sx={{
              display: { md: showBackButton ? 'block' : 'none' },
              bgcolor: 'background.paper',
              boxShadow: 1,
              zIndex: theme.zIndex.drawer + 1
            }}
          >
            <Toolbar>
              {isMobile && (
                <IconButton
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2, color: 'text.primary' }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              {showBackButton && onBack && (
                <IconButton
                  edge="start"
                  onClick={onBack}
                  sx={{ mr: 2, color: 'text.primary' }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              {title && (
                <Typography variant="h6" sx={{ flexGrow: 1, color: 'text.primary', fontWeight: 600 }}>
                  {title}
                </Typography>
              )}
            </Toolbar>
          </AppBar>
        )}

        {/* Content */}
        <Box sx={{ pt: { xs: isMobile ? 8 : 0, md: 0 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
